// import Header from "@/components/header";
// import GlucoseGraph from "@/components/glucose-graph";
// import ConnectCGM from "@/components/connect-cgm";
import HeroGirlImage from "src/assets/images/cgm/connection-journey/hero-image-guide.webp";
import FeedbackImage from "src/assets/images/cgm/connection-journey/cgm-guide-feedabck.webp";
import PatternsImage from "src/assets/images/cgm/connection-journey/cgm-guide-spot-patterns.webp";
import ControlImage from "src/assets/images/cgm/connection-journey/cgm-guide-personal-control.webp";
import ScienceImage from "src/assets/images/cgm/connection-journey/cgm-guide-trusted-science.webp";
import { useNavigate, useParams } from "react-router-dom";
import Header from "src/components/app/layout/Header";
import Layout from "src/components/app/layout/Layout";
import InfoCard from "src/components/app/cgm/connection/InfoCard";
import Accordion from "src/components/common/Accordion";
import RangeInfoCard from "src/components/app/cgm/connection/RangeInfoCard";

// src/pages/CGMConnectGuideScreen/data.ts

const actionSections = {
  "Immediate Actions": [
    {
      title: "For low glucose (<70 mg/dL):",
      description:
        "Consume 15g of fast-acting carbohydrates, wait 15 minutes, recheck",
    },
    {
      title: "For high glucose (>250 mg/dL):",
      description:
        "Check for ketones, take correction dose if appropriate, stay hydrated",
    },
    {
      title: "For rapidly falling glucose:",
      description: "Consume carbohydrates proactively to prevent lows",
    },
    {
      title: "For rapidly rising glucose:",
      description: "Consider correction dose, physical activity, or hydration",
    },
  ],
  "Short-Term Adjustments": [
    {
      title: "Adjust insulin for meals:",
      description:
        "Modify mealtime insulin based on carb intake and glucose trends",
    },
    {
      title: "Change activity level:",
      description:
        "Increase or decrease physical activity to help manage glucose",
    },
    {
      title: "Modify snack timing:",
      description: "Adjust snack times to prevent overnight lows or highs",
    },
  ],
  "Long-Term Strategies": [
    {
      title: "Review patterns:",
      description: "Analyze glucose logs to identify recurring trends",
    },
    {
      title: "Consult healthcare provider:",
      description:
        "Discuss persistent issues or frequent adjustments with your care team",
    },
    {
      title: "Update care plan:",
      description:
        "Revise insulin-to-carb ratios, correction factors, or basal rates as needed",
    },
  ],
};

const infoCardsData = [
  {
    image: (
      <img
        src={FeedbackImage}
        alt="Live Feedback"
        className="w-20 h-20 object-contain mx-auto"
      />
    ),
    title: "Live Feedback",
    description:
      "Know how your body responds to food, stress, and sleep in real time.",
  },
  {
    image: (
      <img
        src={PatternsImage}
        alt="Spot Patterns"
        className="w-20 h-20 object-contain mx-auto"
      />
    ),
    title: "Spot Patterns",
    description: "Identify habits that spike or crash your energy and mood.",
  },
  {
    image: (
      <img
        src={ControlImage}
        alt="Personal Control"
        className="w-20 h-20 object-contain mx-auto"
      />
    ),
    title: "Personal Control",
    description:
      "Stay in range, feel better daily, and avoid long-term health risks.",
  },
  {
    image: (
      <img
        src={ScienceImage}
        alt="Trusted Science"
        className="w-20 h-20 object-contain mx-auto"
      />
    ),
    title: "Trusted Science",
    description:
      "Used by athletes, doctors, and nutritionists for precision tracking.",
  },
];

const patternRecognitionData = [
  {
    title: "Early Morning Glucose Rise (4–8 AM)",
    description:
      "This is known as the <b>Dawn Phenomenon</b>. The body releases hormones like cortisol and growth hormone early in the morning, which can cause your liver to release more glucose—leading to a natural rise in blood sugar before you wake up",
  },
  {
    title: "Post-Meal Glucose Spikes",
    description:
      "After you eat—especially meals high in carbohydrates—your glucose levels tend to rise sharply. This peak usually occurs <b>1–2 hours after eating</b> as your body digests food and absorbs glucose into the bloodstream.",
  },
  {
    title: "Low Glucose During Sleep (12–3 AM)",
    description:
      "Blood sugar can drop overnight due to <b>long gaps without food</b> or if insulin or medications peak during this time. It’s a common pattern for those who take insulin or skip a bedtime snack.",
  },
  {
    title: "Glucose Drop from Physical Activity",
    description:
      "Exercise helps muscles use up glucose for energy, which naturally <b>lowers your blood sugar</b> levels during and after activity. This effect can vary depending on the type and duration of exercise.",
  },
  {
    title: "Glucose Rise from Stress",
    description:
      "Physical or emotional stress triggers the release of stress hormones like adrenaline and cortisol. These hormones signal the liver to produce more glucose, causing a <b>temporary rise in blood sugar</b> levels even without eating.",
  },
];

const frequntlyAskedData = [
  {
    title:
      "How does the GoodFlip Powered CGM measure glucose compared to traditional Blood Glucose Monitors (BGMs)?",
    description:
      "GoodFlip Powered CGM measures glucose in <b>interstitial fluid</b>, not blood. This creates a <b>time lag</b>, especially after meals, as blood glucose spikes faster than interstitial glucose.",
  },
  {
    title: "What is the accuracy of the GoodFlip Powered CGM in terms of MARD?",
    description:
      "GoodFlip Powered has a <b>Mean Absolute Relative Difference (MARD) below 9%</b>, aligning with industry standards. This means its readings are typically within 9% of those from high-quality BGMs like Accu-Chek or OneTouch.",
  },
  {
    title:
      "Why does GoodFlip Powered CGM show higher glucose readings than some other CGMs like Libre?",
    description:
      "The GoodFlip Powered intentionally <b>does not adjust readings downward</b> to prevent under-reporting, which could lead to insulin overdosing. Hence, readings may appear <b>slightly higher.</b>",
  },
  {
    title: "Is the GoodFlip Powered CGM clinically validated?",
    description:
      "Yes, GoodFlip Powered is <b>clinically validated</b>, has undergone <b>extensive testing</b>, and has received <b>positive feedback</b> from users on performance and reliability.",
  },
  {
    title: "Can external factors affect CGM accuracy?",
    description:
      "Yes. <b>Sensor placement</b> and <b>external conditions</b> like<b> moisture</b> can affect accuracy. Correct <b>insertion and maintenance</b> are essential for optimal performance.",
  },
  {
    title:
      "What protocol was suggested to further validate GoodFlip Powered CGM’s accuracy?",
    description:
      "A proposal was made to conduct <b>controlled experiments</b> with multiple users, comparing GoodFlip Powered CGM and BGM readings under standardized conditions.",
  },
  {
    title: "How does GoodFlip Powered defect rate compare to other CGMs?",
    description:
      "<ul><li>Libre Pro: ~16%</li><li>Libre: ~10–11%</li><li><b>GoodFlip Powered CGM: ~2–3%</b></li><li>This indicates a <b>significantly lower defect rate.</b> There’s a need for a clear <b>SOP (Standard Operating Procedure)</b> to manage and track defects.</li></ul>",
  },
  {
    title:
      "Why is a 24-hour calibration period needed after inserting the CGM sensor?",
    description:
      "Sensor insertion causes <b>tissue inflammation</b>, disrupting glucose readings. It takes ~24 hours for the body to adjust. Users should <b>not rely on readings</b> during this period. A <b>disclaimer</b> is recommended to inform users of this.",
  },
];

// Example data
const rangeCards = [
  {
    title: "Time in Range (TIR)",
    description:
      "Percentage of time your glucose levels stay within the target range (70-180 mg/dL).",
    target: "Target: >70% of your day",
  },
  {
    title: "Time in Range (TIR)",
    description:
      "Percentage of time your glucose levels stay within the target range (70-180 mg/dL).",
    target: "Target: >70% of your day",
  },
  // Add more cards as needed
];

function chunkArray(array: any[], size: number) {
  const result = [];
  for (let i = 0; i < array.length; i += size) {
    result.push(array.slice(i, i + size));
  }
  return result;
}

const CGMConnectGuideScreen: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams();
  const supportProgramKey = params["support-program-key"] ?? "";
  return (
    <Layout
      supportProgramKey={supportProgramKey}
      enableTheme={true}
      containerClassName="bg-white"
      className="bg-white"
      header={
        <Header
          title="Back"
          showBackButton={true}
          className="bg-white"
          onBackClick={() => {
            navigate(-1);
          }}
        />
      }
    >
      <img src={HeroGirlImage} className="w-full h-auto" />
      <div className="px-4 flex flex-col items-center justify-center mt-6">
        <h2 className="mb-4 font-semibold text-[24px] leading-[32px] tracking-[0.25px] text-[#1A2530]">
          What is Continuous Glucose Monitoring?
        </h2>
        <p className="font-roboto font-normal text-[14px] leading-[21px] tracking-[0.25px] text-[#6B7280] mt-3">
          Continuous Glucose Monitoring (CGM) is a technology that measures your
          glucose levels throughout the day and night. Unlike traditional
          fingerstick measurements that provide single snapshots of your
          glucose, CGM provides a continuous stream of data.
        </p>
      </div>
      {/* Why Use a CGM Section */}
      <div className="my-10 px-4">
        <h2 className="font-semibold text-[24px] leading-[32px] tracking-[0.25px] text-[#1A2530] mb-4">
          Why Use a CGM?
        </h2>
        <div className="flex flex-col items-center gap-4">
          {chunkArray(infoCardsData, 2).map((row: any[], rowIdx: number) => (
            <div
              key={rowIdx}
              className="flex flex-row justify-center gap-4 w-full"
            >
              {row.map((card: any) => (
                <InfoCard
                  key={card.title}
                  image={card.image}
                  title={card.title}
                  description={card.description}
                  className="flex-1 min-w-[170px]"
                />
              ))}
            </div>
          ))}
        </div>
        <div className="w-full mt-8">
          <h2 className="font-semibold text-[24px] leading-[32px] tracking-[0.25px] text-[#1A2530] mb-4">
            Track Your Key Metrics
          </h2>
          <div className="flex gap-4 overflow-x-auto pb-2">
            {rangeCards.map((card, idx) => (
              <RangeInfoCard
                key={idx}
                title={card.title}
                description={card.description}
                target={card.target}
              />
            ))}
          </div>
        </div>
        <div className="w-full mt-8 space-y-4">
          <h2 className="font-semibold text-[24px] leading-[32px] tracking-[0.25px] text-[#1A2530] mb-4">
            Pattern Recognition
          </h2>
          {patternRecognitionData.map((item, idx) => (
            <Accordion
              key={idx}
              title={item.title}
              description={item.description}
            />
          ))}
        </div>

        <div className="w-full mt-8 space-y-4">
          <h2 className="font-semibold text-[24px] leading-[32px] tracking-[0.25px] text-[#1A2530] mb-4">
            Frequently Asked Questions
          </h2>
          {frequntlyAskedData.map((item, idx) => (
            <Accordion
              key={idx}
              title={item.title}
              description={item.description}
            />
          ))}
        </div>

        {/* <div className="w-full mt-8">
          <div className="space-y-8">
            {Object.entries(actionSections).map(([section, cards]) => (
              <div key={section}>
                <h2 className="mb-4  text-[#667085] font-roboto font-normal text-[14px] leading-[21px] tracking-[0.25px]">
                  {section}
                </h2>
                <div className="space-y-4">
                  {cards.map((card, idx) => (
                    <ActionCard
                      key={idx}
                      title={card.title}
                      description={card.description}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div> */}
      </div>
    </Layout>
  );
};

export default CGMConnectGuideScreen;
